import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

export default function ResetPasswordPage() {
	const navigate = useNavigate();

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const [checkingSession, setCheckingSession] = useState(true);
	const [recoveryValid, setRecoveryValid] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);

	// --------------------------------------------------
	// Vérification de la session de récupération Supabase
	// --------------------------------------------------

	useEffect(() => {
		let mounted = true;

		const checkRecoverySession = async () => {
			try {
				const {
					data: { session },
				} = await supabase.auth.getSession();

				if (!mounted) return;

				if (session?.user) {
					setRecoveryValid(true);
				}
			} catch (error) {
				console.error("Erreur vérification session récupération:", error);
			} finally {
				if (mounted) {
					setCheckingSession(false);
				}
			}
		};

		// Supabase peut créer la session après l'ouverture
		// de la page depuis le lien reçu par email.
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			if (!mounted) return;

			if (event === "PASSWORD_RECOVERY" || (session?.user && !recoveryValid)) {
				setRecoveryValid(true);
				setCheckingSession(false);
			}
		});

		checkRecoverySession();

		return () => {
			mounted = false;
			subscription.unsubscribe();
		};
	}, []);

	// --------------------------------------------------
	// Validation
	// --------------------------------------------------

	const passwordIsValid = password.length >= 8;
	const passwordsMatch =
		password.length > 0 &&
		confirmPassword.length > 0 &&
		password === confirmPassword;

	// --------------------------------------------------
	// Changement du mot de passe
	// --------------------------------------------------

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!passwordIsValid) {
			toast.error("Le mot de passe doit contenir au moins 8 caractères.");
			return;
		}

		if (password !== confirmPassword) {
			toast.error("Les mots de passe ne correspondent pas.");
			return;
		}

		try {
			setIsSubmitting(true);

			const { error } = await supabase.auth.updateUser({
				password,
			});

			if (error) {
				throw error;
			}

			setSuccess(true);

			toast.success("Votre mot de passe a été modifié avec succès.");
		} catch (error) {
			console.error("Erreur modification mot de passe:", error);

			toast.error(
				error?.message || "Impossible de modifier votre mot de passe.",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// --------------------------------------------------
	// Chargement
	// --------------------------------------------------

	if (checkingSession) {
		return (
			<main className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center px-6">
				<div className="text-center">
					<div className="w-9 h-9 border-2 border-border-subtle border-t-latte-gold rounded-full animate-spin mx-auto mb-4" />

					<p className="text-sm text-soft-charcoal-secondary">
						Vérification du lien...
					</p>
				</div>
			</main>
		);
	}

	// --------------------------------------------------
	// Lien invalide / expiré
	// --------------------------------------------------

	if (!recoveryValid) {
		return (
			<main className="min-h-screen pt-20 bg-warm-alabaster">
				<div className="max-w-xl mx-auto px-6 py-16 lg:py-24">
					<div className="bg-white border border-border-subtle rounded-2xl p-8 lg:p-10 text-center">
						<div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-6">
							<AlertCircle className="w-6 h-6" />
						</div>

						<h1 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
							Lien invalide ou expiré
						</h1>

						<p className="text-soft-charcoal-secondary mt-3">
							Ce lien de modification du mot de passe n'est plus valide. Vous
							pouvez demander un nouveau lien depuis la page de connexion.
						</p>

						<Button
							type="button"
							onClick={() => navigate("/connexion")}
							className="btn-primary mt-8"
						>
							Retour à la connexion
						</Button>
					</div>
				</div>
			</main>
		);
	}

	// --------------------------------------------------
	// Succès
	// --------------------------------------------------

	if (success) {
		return (
			<main className="min-h-screen pt-20 bg-warm-alabaster">
				<div className="max-w-xl mx-auto px-6 py-16 lg:py-24">
					<div className="bg-white border border-border-subtle rounded-2xl p-8 lg:p-10 text-center">
						<div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-6">
							<CheckCircle2 className="w-7 h-7" />
						</div>

						<p className="text-xs uppercase tracking-[0.16em] text-latte-gold font-semibold mb-2">
							Sécurité
						</p>

						<h1 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
							Mot de passe modifié
						</h1>

						<p className="text-soft-charcoal-secondary mt-3">
							Votre nouveau mot de passe a bien été enregistré.
						</p>

						<Button
							type="button"
							onClick={() => navigate("/mon-compte")}
							className="btn-primary mt-8"
						>
							Retour à mon compte
						</Button>
					</div>
				</div>
			</main>
		);
	}

	// --------------------------------------------------
	// Formulaire
	// --------------------------------------------------

	return (
		<main className="min-h-screen pt-20 bg-warm-alabaster">
			<div className="max-w-xl mx-auto px-6 py-16 lg:py-24">
				<div className="bg-white border border-border-subtle rounded-2xl overflow-hidden shadow-sm">
					<div className="p-8 lg:p-10">
						<div className="w-12 h-12 rounded-full bg-warm-secondary text-latte-gold flex items-center justify-center mb-6">
							<KeyRound className="w-5 h-5" />
						</div>

						<p className="text-xs uppercase tracking-[0.16em] text-latte-gold font-semibold mb-2">
							Sécurité du compte
						</p>

						<h1 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
							Nouveau mot de passe
						</h1>

						<p className="text-soft-charcoal-secondary mt-3">
							Choisissez un nouveau mot de passe pour sécuriser votre compte.
						</p>

						<form onSubmit={handleSubmit} className="mt-8 space-y-6">
							{/* Nouveau mot de passe */}

							<div className="space-y-2">
								<Label htmlFor="new-password">Nouveau mot de passe</Label>

								<div className="relative">
									<Input
										id="new-password"
										type={showPassword ? "text" : "password"}
										value={password}
										onChange={(event) => setPassword(event.target.value)}
										minLength={8}
										required
										autoComplete="new-password"
										placeholder="Minimum 8 caractères"
										className="input-field pr-12"
									/>

									<button
										type="button"
										onClick={() => setShowPassword((prev) => !prev)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-soft-charcoal-secondary hover:text-espresso"
										aria-label={
											showPassword
												? "Masquer le mot de passe"
												: "Afficher le mot de passe"
										}
									>
										{showPassword ? (
											<EyeOff className="w-4 h-4" />
										) : (
											<Eye className="w-4 h-4" />
										)}
									</button>
								</div>

								<p className="text-xs text-soft-charcoal-secondary">
									Minimum 8 caractères.
								</p>
							</div>

							{/* Confirmation */}

							<div className="space-y-2">
								<Label htmlFor="confirm-password">
									Confirmer le mot de passe
								</Label>

								<div className="relative">
									<Input
										id="confirm-password"
										type={showConfirmPassword ? "text" : "password"}
										value={confirmPassword}
										onChange={(event) => setConfirmPassword(event.target.value)}
										minLength={8}
										required
										autoComplete="new-password"
										placeholder="Répétez le mot de passe"
										className="input-field pr-12"
									/>

									<button
										type="button"
										onClick={() => setShowConfirmPassword((prev) => !prev)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-soft-charcoal-secondary hover:text-espresso"
										aria-label={
											showConfirmPassword
												? "Masquer le mot de passe"
												: "Afficher le mot de passe"
										}
									>
										{showConfirmPassword ? (
											<EyeOff className="w-4 h-4" />
										) : (
											<Eye className="w-4 h-4" />
										)}
									</button>
								</div>

								{confirmPassword && !passwordsMatch && (
									<p className="text-xs text-red-600">
										Les mots de passe ne correspondent pas.
									</p>
								)}
							</div>

							<Button
								type="submit"
								disabled={isSubmitting || !passwordIsValid || !passwordsMatch}
								className="btn-primary w-full"
							>
								{isSubmitting
									? "Modification..."
									: "Enregistrer mon nouveau mot de passe"}
							</Button>
						</form>
					</div>
				</div>
			</div>
		</main>
	);
}
